import { AccountStatus, AccountsStatusCounts } from '../accounts-overview/accounts-overview.types';

/** One layer of the pie: a full disc, or a slice from twelve o'clock clockwise. */
export interface StatusPieLayer {
    status: AccountStatus;
    /** SVG path of the slice in a 16×16 box; null draws the full disc. */
    path: string | null;
}

const CENTER = 8;
const RADIUS = 8;

/**
 * The pie as stacked layers, back to front: grey to 100 %, red to reconciled + difference, green to
 * reconciled. Each layer starts at twelve o'clock and covers the one behind it, so every border
 * between two colours is a single edge. Slices laid side by side would leave a light seam where
 * their anti-aliased edges meet.
 */
export function statusPieLayers(counts: AccountsStatusCounts): StatusPieLayer[] {
    const total = counts.reconciled + counts.difference + counts.open;
    if (total === 0) {
        return [];
    }

    const layers: [AccountStatus, number, number][] = [
        ['open', counts.open, 1],
        ['difference', counts.difference, (counts.reconciled + counts.difference) / total],
        ['reconciled', counts.reconciled, counts.reconciled / total],
    ];

    return layers
        .filter(([, count]) => count > 0)
        .map(([status, , share]) => ({ status, path: share >= 1 ? null : slicePath(share) }));
}

function slicePath(share: number): string {
    const angle = share * 2 * Math.PI;
    const x = round(CENTER + RADIUS * Math.sin(angle));
    const y = round(CENTER - RADIUS * Math.cos(angle));
    const largeArc = share > 0.5 ? 1 : 0;

    return `M${CENTER} ${CENTER} L${CENTER} 0 A${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${x} ${y} Z`;
}

function round(value: number): number {
    return Math.round(value * 1000) / 1000;
}
